import React from 'react';
import { Button } from '@carbon/react';
import { Checkmark } from '@carbon/react/icons';
import { useCrudConfig } from './provider';

export interface CrudPanelShellProps {
  /** Panel heading, already translated. */
  title: string;
  /** Extra class for the panel root, e.g. its width rules. */
  className?: string;
  children: React.ReactNode;
  /** Commit the staged edits and close. */
  onApply: () => void;
  /** Return the staged edits to the resource's defaults. Does not close. */
  onReset: () => void;
  /** Disables Reset when there is nothing to return to defaults. */
  resetDisabled?: boolean;
}

/**
 * The shared shell for the toolbar's two popovers — filters and columns. Both edit a
 * STAGED copy of their state, so the panel needs somewhere to put the commit: hence a
 * fixed header/body/footer rather than each panel inventing its own arrangement.
 *
 * Reset does not close. Apply does — a panel that stayed open after committing left the
 * table it just changed hidden behind it.
 */
export const CrudPanelShell = ({
  title,
  className,
  children,
  onApply,
  onReset,
  resetDisabled,
}: CrudPanelShellProps) => {
  const { translate } = useCrudConfig();
  return (
    <div className={['crud-panel', 'crud-panel--shell', className].filter(Boolean).join(' ')}>
      <div className="crud-panel__header">{title}</div>
      <div className="crud-panel__body">{children}</div>
      <div className="crud-panel__footer">
        <Button kind="ghost" size="sm" onClick={onReset} disabled={resetDisabled}>
          {translate('actions.reset')}
        </Button>
        <Button kind="primary" size="sm" renderIcon={Checkmark} onClick={onApply}>
          {translate('actions.apply')}
        </Button>
      </div>
    </div>
  );
};
